import { useQuery } from '@tanstack/react-query'
import * as R from 'remeda'
import { useMemo } from 'react'
import { toast } from 'sonner'
import { useGetShopifyOrder } from '~/features/shopify/hooks/useGetShopifyOrder'
import { supabaseApi } from '~/supabase/api'

export const GET_SHOPIFY_SALES_QUERY = 'get-shopify-order'

export const useGetOrders = () => {
  const { data: orders } = useQuery({
    queryKey: ['get-orders'],
    queryFn: async () => {
      const { data, error } = await supabaseApi.from('orders').select('*')

      if (error) {
        toast.error(error.message)
        throw error
      }

      return data
    },
  })

  const { data: shopifySales } = useQuery({
    queryKey: [GET_SHOPIFY_SALES_QUERY],
    queryFn: async () => {
      const { data, error } = await supabaseApi
        .from('shopify_sales')
        .select('*, shopify_order_id(*), visitor(*)')

      if (error) {
        toast.error(error.message)
        throw error
      }

      return data
    },
    select: (response) =>
      R.mapToObj(response, (item) => [item.shopify_order_id.order_id, item.visitor]),
  })

  const { orderIds, orderGroup } = useMemo(() => {
    if (!orders) return { orderIds: [], orderGroup: {} }
    const orderGroup = R.pipe(
      orders,
      R.filter((d) => d.status === 'to_review'),
      R.mapToObj((d) => [d.order_id, d])
    )

    return { orderGroup, orderIds: R.keys(orderGroup) }
  }, [orders])

  const { data: shopifyOrders, isLoading } = useGetShopifyOrder(orderIds)

  const data = useMemo(() => {
    if (!shopifyOrders) return []

    return R.pipe(
      shopifyOrders,
      R.map((order) => {
        const supabaseOrder = orderGroup[order.id]
        if (!supabaseOrder) return null
        const status = supabaseOrder.status
        const shopify_order_id = supabaseOrder.id
        const visitor = shopifySales ? shopifySales[order.id] : undefined
        return {
          ...order,
          status,
          visitor,
          shopify_order_id,
        }
      }),
      R.filter(R.isNonNullish)
    )
  }, [orderGroup, shopifyOrders, shopifySales])

  return { data, isLoading }
}
